import { type ProductInformation, useProductCatalogRepo } from '@hotjar/product-catalog';
import type {
  ChurnDeflectionObservePlan,
  ChurnDeflectionAskPlan,
  ChurnDeflectionProducts,
  ProductAllowance,
  ProductCatalogOptions,
} from '../types';

type ObserveProductInformation = Omit<ProductInformation, 'allowance'> & {
  allowance: {
    dailyCcSessions: number;
  };
};

type AskProductInformation = Omit<ProductInformation, 'allowance'> & {
  allowance: {
    monthlyResponses: number;
  };
};

function mapObservePlan({
  plan,
  allowance,
}: ObserveProductInformation): ChurnDeflectionObservePlan {
  return {
    name: plan.name,
    dailyAllowance: allowance.dailyCcSessions,
  };
}

function mapAskPlan({ plan, allowance }: AskProductInformation): ChurnDeflectionAskPlan {
  return {
    name: plan.name,
    monthlyAllowance: allowance.monthlyResponses,
  };
}

function useCurrentPlans(
  products: ProductAllowance[],
  productCatalogOptions: ProductCatalogOptions,
): Record<string, never> {
  const { getProductBySku } = useProductCatalogRepo(productCatalogOptions);

  return products.reduce((acc, item) => {
    const productInformation = getProductBySku(item.sku);

    if (!productInformation) return acc;

    const { product } = productInformation;

    if (product.name === 'observe') {
      return {
        ...acc,
        observe: mapObservePlan(productInformation as ObserveProductInformation),
      };
    }

    if (product.name === 'ask') {
      return {
        ...acc,
        ask: mapAskPlan(productInformation as AskProductInformation),
      };
    }

    return acc;
  }, {});
}

function useBasicPlans(productCatalogOptions: ProductCatalogOptions): Record<string, never> {
  const { getProductBySku, getBasicAllowanceSkus } = useProductCatalogRepo(productCatalogOptions);

  const basicAllowancesSkus = getBasicAllowanceSkus() ?? [];

  return basicAllowancesSkus.reduce((acc, sku) => {
    const productInformation = getProductBySku(sku);

    if (!productInformation) return acc;

    const { product } = productInformation;

    if (product.name === 'observe') {
      return {
        ...acc,
        observe: mapObservePlan(productInformation as ObserveProductInformation),
      };
    }

    if (product.name === 'ask') {
      return {
        ...acc,
        ask: mapAskPlan(productInformation as AskProductInformation),
      };
    }

    return acc;
  }, {});
}

/**
 * Hook that maps the necessary data of the current products that are going to be downgraded,
 * to be easily used in the component's view.
 */
export function useProductsToDowngrade(
  products: ProductAllowance[],
  productCatalogOptions: ProductCatalogOptions,
): ChurnDeflectionProducts | null {
  const basicPlans = useBasicPlans(productCatalogOptions);
  const currentPlans = useCurrentPlans(products, productCatalogOptions);

  const isDowngradingAsk = currentPlans.ask != null;

  if (currentPlans.observe == null) {
    return null;
  }

  return {
    observe: {
      currentPlan: currentPlans.observe,
      newPlan: basicPlans.observe,
    },
    ask: isDowngradingAsk
      ? {
          currentPlan: currentPlans.ask,
          newPlan: basicPlans.ask,
        }
      : undefined,
  };
}
